import React, {useEffect, useMemo} from 'react';
import {divIcon, LatLng, latLngBounds} from 'leaflet';
import {CircleMarker, Pane, Polyline, Tooltip, useMap} from 'react-leaflet';
import {Box} from '@mui/material';
import LeafletContainer from "@ui/leafletCustom/leafletContainer/LeafletContainer";
import {ISingleVoyage} from "@containers/singleVoyage/types/voyage";
import {
	ISyncChangeVoyageTemplate,
	IVoyageTemplateSingleVoyage
} from "@containers/singleVoyage/voyageTemplates/types";
import {
	buildRouteChangeVoyageTemplate
} from "@containers/singleVoyage/voyageTemplates/mutateTemplate/helpers/routeChange";
import RouteArrowsSyncVoyageTemplate
	from "@containers/singleVoyage/voyageTemplates/sync/map/RouteArrowsSyncVoyageTemplate";
import LeafletMarker from "@ui/leafletCustom/leafletMarker/LeafletMarker";
import {
	ROUTE_CHANGE_COLOR,
	ROUTE_CHANGE_PATH,
	RING_RADIUS_ROUTE_CHANGE
} from "@containers/singleVoyage/voyageTemplates/routeChange/routeChangeStyle";
import MovedStopsRouteChange, {
	IMovedStopRouteChange
} from "@containers/singleVoyage/voyageTemplates/routeChange/MovedStopsRouteChange";
import {sx} from './sx'

/**
 * A numbered stop in the stop line's look: white with a green ring when untouched, yellow when
 * added, orange when moved or changed.
 */
const stopIcon = (number: number, change?: string) => {
	const fill = change === 'ADD' ? ROUTE_CHANGE_COLOR.added : change ? ROUTE_CHANGE_COLOR.changed : '#fff'
	const text = change && change !== 'ADD' ? '#fff' : '#222'
	const ring = change ? '#fff' : ROUTE_CHANGE_COLOR.newRoute
	const size = change ? 24 : 22
	return divIcon({
		className: '',
		iconSize: [size, size],
		iconAnchor: [size / 2, size / 2],
		html: `<div style="width:${size}px;height:${size}px;border-radius:50%;box-sizing:border-box;`
			+ `background:${fill};border:2px solid ${ring};color:${text};display:flex;align-items:center;`
			+ `justify-content:center;font:700 11px/1 sans-serif;box-shadow:0 1px 3px rgba(0,0,0,.35)">${number}</div>`,
	})
}

/** An old stop closer than this to a template stop is the same stop, not a moved one. */
const SAME_PLACE_METRES = 15

const MAP_SIZE = {
	width: '100%',
	height: '100%',
}

interface IStopMarker {
	position: LatLng
	name: string
	ordinal: number | null
	waypointTemplateId?: number | null
}

const toLatLngs = (coordinates?: number[][]): LatLng[] => {
	if (!coordinates?.length) {
		return []
	}
	return coordinates.map(([lng, lat]) => new LatLng(lat, lng))
}

const toLatLng = (coordinates?: number[]): LatLng | null => {
	if (!coordinates || coordinates.length < 2) {
		return null
	}
	return new LatLng(coordinates[1], coordinates[0])
}

/** Fits the template route once the map is laid out inside the modal. */
const FitBoundsSyncVoyageTemplate = ({positions}: { positions: LatLng[] }) => {
	const map = useMap()

	// refits whenever the container settles on a new size: the modal animates in on open and can
	// switch to fullscreen, and leaflet notices neither on its own
	useEffect(() => {
		if (positions.length < 2) {
			return
		}
		let timeoutId: ReturnType<typeof setTimeout>
		const refit = () => {
			clearTimeout(timeoutId)
			timeoutId = setTimeout(() => {
				map.invalidateSize()
				map.fitBounds(latLngBounds(positions), {padding: [20, 20]})
			}, 200)
		}
		const observer = new ResizeObserver(refit)
		observer.observe(map.getContainer())
		refit()
		return () => {
			clearTimeout(timeoutId)
			observer.disconnect()
		}
	}, [map, positions])

	return <></>
}

/** A stop the stop line points at: one of the template's, or one the voyage had (a removed stop). */
export interface IHighlightedStopSync {
	ordinal: number
	old?: boolean
}

/** Rings the stop under the cursor in the stop line, and brings it into view if it is off screen. */
const HighlightSyncVoyageTemplate = ({position, name}: { position: LatLng, name: string }) => {
	const map = useMap()

	useEffect(() => {
		if (!map.getBounds().contains(position)) {
			map.panTo(position)
		}
	}, [map, position])

	return (
		<CircleMarker
			center={position}
			radius={20}
			pathOptions={{color: '#1E1E1E', weight: 3, fillColor: '#1E1E1E', fillOpacity: 0.08}}
		>
			<Tooltip direction={'top'} offset={[0, -18]} permanent>{name}</Tooltip>
		</CircleMarker>
	)
}

interface IProps {
	template: IVoyageTemplateSingleVoyage
	/** the voyage under review; null while it loads */
	voyage: ISingleVoyage | null
	changes: ISyncChangeVoyageTemplate[]
	highlighted?: IHighlightedStopSync | null
}

/**
 * The template route over the route of the voyage under the cursor. Voyages of a template are its
 * copies, so the wide grey line is what the template looked like before the edit; stops the edit
 * added or changed are coloured, and old positions show only where a stop moved or was removed.
 */
const SyncMapVoyageTemplate = ({template, voyage, changes, highlighted}: IProps) => {

	const newRoute = useMemo(() => toLatLngs(template?.routeShape?.geom?.coordinates), [template])

	const newStops = useMemo((): IStopMarker[] => {
		return (template?.waypoints ?? [])
			.filter((waypoint) => waypoint.waypointTemplate)
			.map((waypoint, ordinal) => ({
				position: waypoint.waypointTemplate.geom?.type === 'Point'
					? toLatLng(waypoint.waypointTemplate.geom.coordinates)
					: null,
				name: waypoint.waypointTemplate.name,
				ordinal,
				waypointTemplateId: waypoint.waypointTemplateId,
			}))
			.filter((stop) => stop.position)
	}, [template])

	const isCurrentVoyage = !!voyage

	const oldRoute = useMemo(() => {
		if (!isCurrentVoyage || voyage?.routeShape?.geom?.type !== 'LineString') {
			return []
		}
		return toLatLngs(voyage.routeShape.geom.coordinates)
	}, [voyage, isCurrentVoyage])

	const oldStops = useMemo((): IStopMarker[] => {
		if (!isCurrentVoyage) {
			return []
		}
		return (voyage?.waypoints ?? [])
			.filter((waypoint) => waypoint.type !== 'SHAPING_POINT'
				&& waypoint.status !== 'DELETED'
				&& waypoint.geom?.type === 'Point')
			.map((waypoint) => ({
				position: toLatLng(waypoint.geom?.coordinates),
				name: waypoint.name,
				ordinal: waypoint.plannedOrdinal,
				waypointTemplateId: waypoint.waypointTemplate?.id,
			}))
			.filter((stop) => stop.position)
			.filter((stop) => !newStops.some((it) => it.position.distanceTo(stop.position) < SAME_PLACE_METRES))
	}, [voyage, isCurrentVoyage, newStops])

	// stretches of the template route the voyage did not run along
	const changedSegments = useMemo(() => {
		if (oldRoute.length < 2 || newRoute.length < 2) {
			return []
		}
		return buildRouteChangeVoyageTemplate({previous: oldRoute, current: newRoute, points: []}).changedSegments
	}, [oldRoute, newRoute])

	const changeByNewOrdinal = useMemo(() => {
		const result: Record<number, string> = {}
		changes.filter((change) => change.type !== 'REMOVE' && change.ordinal !== null)
			.forEach((change) => result[change.ordinal] = change.type)
		return result
	}, [changes])

	const removedOrdinals = useMemo(() => {
		return new Set(changes.filter((change) => change.type === 'REMOVE').map((change) => change.ordinal))
	}, [changes])

	const movedStops = useMemo((): IMovedStopRouteChange[] => {
		return changes.filter((change) => change.type === 'MOVE' && change.ordinal !== null)
			.map((change) => {
				const stop = newStops.find((it) => it.ordinal === change.ordinal)
				if (!stop) {
					return null
				}
				const before = oldStops.find((it) => it.waypointTemplateId && it.waypointTemplateId === stop.waypointTemplateId)
				return {
					key: change.ordinal,
					number: change.ordinal + 1,
					position: stop.position,
					before: before?.position ?? null,
					distance: change.distanceMeters,
				}
			})
			.filter((stop) => !!stop)
	}, [changes, newStops, oldStops])

	// old spots of moved stops are drawn with their stop; the rest are removed or simply left behind
	const pairedOldPositions = useMemo(() => {
		return new Set(movedStops.map((stop) => stop.before).filter(Boolean))
	}, [movedStops])

	const highlightedStop = useMemo(() => {
		if (!highlighted) {
			return null
		}
		if (highlighted.old) {
			const stop = (voyage?.waypoints ?? []).find((waypoint) => waypoint.plannedOrdinal === highlighted.ordinal
				&& waypoint.type !== 'SHAPING_POINT' && waypoint.geom?.type === 'Point')
			const position = stop && toLatLng(stop.geom.coordinates)
			return position ? {position, name: stop.name} : null
		}
		const stop = newStops.find((it) => it.ordinal === highlighted.ordinal)
		return stop ? {position: stop.position, name: `${stop.ordinal + 1}. ${stop.name}`} : null
	}, [highlighted, newStops, voyage])

	const fitPositions = useMemo(() => {
		return newRoute.length ? newRoute : newStops.map((it) => it.position)
	}, [newRoute, newStops])

	const center = fitPositions[0]

	if (!center) {
		return <></>
	}

	return (
		<Box sx={sx.root}>
			<LeafletContainer
				center={[center.lat, center.lng]}
				size={MAP_SIZE}
				hasFullScreen
				hasSearchbar={false}
				zoomControl
			>
				<FitBoundsSyncVoyageTemplate positions={fitPositions}/>

				{/* panes fix the stacking: the old route arrives later but must stay underneath */}
				<Pane name={'syncOldRoute'} style={{zIndex: 390}}>
					{oldRoute.length > 1 && <Polyline
                        positions={oldRoute}
                        pathOptions={ROUTE_CHANGE_PATH.oldRoute}
                    />}
				</Pane>

				{newRoute.length > 1 && <Polyline
                    positions={newRoute}
                    pathOptions={ROUTE_CHANGE_PATH.newRoute}
                />}

				<Pane name={'syncChangedRoute'} style={{zIndex: 410}}>
					{changedSegments.map((segment, index) => (
						<Polyline
							key={index}
							positions={segment}
							pathOptions={ROUTE_CHANGE_PATH.changed}
						/>
					))}
				</Pane>

				<Pane name={'syncArrows'} style={{zIndex: 415}}>
					<RouteArrowsSyncVoyageTemplate positions={newRoute} color={ROUTE_CHANGE_COLOR.newRouteArrow}/>
				</Pane>

				<Pane name={'syncStops'} style={{zIndex: 420}}>
					{newStops.map((stop) => (
						<LeafletMarker
							key={`new-${stop.ordinal}`}
							position={stop.position}
							icon={stopIcon(stop.ordinal + 1, changeByNewOrdinal[stop.ordinal])}
						>
							<Tooltip>{`${stop.ordinal + 1}. ${stop.name}`}</Tooltip>
						</LeafletMarker>
					))}
				</Pane>

				<Pane name={'syncHighlight'} style={{zIndex: 440}}>
					{highlightedStop && <HighlightSyncVoyageTemplate
                        position={highlightedStop.position}
                        name={highlightedStop.name}
                    />}
				</Pane>

				<MovedStopsRouteChange stops={movedStops} markersZIndex={420} tooltipOffset={16} permanentLabels={false}/>

				<Pane name={'syncOldStops'} style={{zIndex: 405}}>
					{oldStops.filter((stop) => !pairedOldPositions.has(stop.position)).map((stop, index) => (
						<CircleMarker
							key={`old-${index}`}
							center={stop.position}
							radius={RING_RADIUS_ROUTE_CHANGE}
							pathOptions={removedOrdinals.has(stop.ordinal) ? ROUTE_CHANGE_PATH.removedRing : ROUTE_CHANGE_PATH.oldRing}
						>
							<Tooltip>{stop.name}</Tooltip>
						</CircleMarker>
					))}
				</Pane>
			</LeafletContainer>

		</Box>
	);
};

export default SyncMapVoyageTemplate;
